"use client";

import { ChevronDown } from "lucide-react";
import { IMaskInput } from "react-imask";

export type GccCountry = {
  code: string;
  dialCode: string;
  flag: string;
  digitCount: number[];
  placeholder: string;
};

export const GCC_COUNTRIES: GccCountry[] = [
  {
    code: "SA",
    dialCode: "+966",
    flag: "🇸🇦",
    digitCount: [9, 10],
    placeholder: "5X XXX XXXX",
  },
  {
    code: "AE",
    dialCode: "+971",
    flag: "🇦🇪",
    digitCount: [9, 10],
    placeholder: "5X XXX XXXX",
  },
  {
    code: "KW",
    dialCode: "+965",
    flag: "🇰🇼",
    digitCount: [8],
    placeholder: "XXXX XXXX",
  },
  {
    code: "BH",
    dialCode: "+973",
    flag: "🇧🇭",
    digitCount: [8],
    placeholder: "XXXX XXXX",
  },
  {
    code: "QA",
    dialCode: "+974",
    flag: "🇶🇦",
    digitCount: [8],
    placeholder: "XXXX XXXX",
  },
  {
    code: "OM",
    dialCode: "+968",
    flag: "🇴🇲",
    digitCount: [8],
    placeholder: "XXXX XXXX",
  },
];

// ─── digit helpers ────────────────────────────────────────────────────────────

export const convertArabicDigitsToEnglish = (value: string): string =>
  value.replace(/[\u0660-\u0669]/g, (c) => String(c.charCodeAt(0) - 0x0660));

// Passed to IMask's `prepare` option — runs per character before pattern
// matching, so all mask definitions only need to cover ASCII [0-9].
const prepareChar = (char: string) => convertArabicDigitsToEnglish(char);

// ─── mask helpers (mirrors reference's useGccMaskConfigs + dynamicMask) ──────

// SA/UAE: dynamic — 9 digits normally, 10 when the first digit is '0'
// (local-prefix format). Reactive: re-evaluated on every value change so
// IMask updates options rather than requiring a full remount.
// 9-digit mask uses 'S' (first-char definition) mirroring PHONE_NUMBER_MASK[0]:
//   /(5|٥|0|٠)/ → after prepare converts Arabic → /[50]/
// 10-digit mask uses plain '0' for all slots (first char is already known to be 0).
// KW/BH/QA/OM: 8-digit mask with a custom first-char definition 'F'.
function getActiveMask(dialCode: string, firstDigit: string): string {
  switch (dialCode) {
    case "+966":
    case "+971":
      return firstDigit === "0" ? "000 000 0000" : "S0 000 0000";
    case "+965":
    case "+973":
    case "+968":
    case "+974":
      return "F000 0000";
    default:
      return "00 000 0000";
  }
}

// `prepare` already converted Arabic digits to ASCII, so ranges here only need
// to cover [0-9]. Mirrors reference's gccMaskConfigs first-char restrictions.
function getDefinitions(dialCode: string): Record<string, RegExp> | undefined {
  switch (dialCode) {
    // Mirrors PHONE_NUMBER_MASK[0]: /(5|٥|0|٠)/ — first char must be 5 or 0.
    case "+966":
    case "+971":
      return { S: /[50]/ };
    case "+965":
      return { F: /[569]/ }; // Kuwait:  5, 6, 9
    case "+973":
      return { F: /[1367]/ }; // Bahrain: 1, 3, 6, 7
    case "+968":
      return { F: /[279]/ }; // Oman:    2, 7, 9
    case "+974":
      return { F: /[567]/ }; // Qatar:   5, 6, 7
    default:
      return undefined;
  }
}

// ─── paste detection (mirrors reference's pipe()) ─────────────────────────────

// When the user pastes a full international number (e.g. "+9665XXXXXXXX"),
// auto-select the matching GCC country and strip the dial code so only the
// local number lands in the input — exactly what the reference pipe() does.
function detectGccDialCode(
  raw: string,
): { dialCode: string; localNumber: string } | null {
  const withPlus = raw.trim().startsWith("+") ? raw.trim() : "+" + raw.trim();
  const english = convertArabicDigitsToEnglish(withPlus);
  // Mirrors: value.length > 9 && gccDialCodes.includes(englishValue.slice(0, 4))
  if (english.length <= 9) return null;
  const matched = GCC_COUNTRIES.find((c) => english.startsWith(c.dialCode));
  if (!matched) return null;
  return {
    dialCode: matched.dialCode,
    localNumber: english.slice(matched.dialCode.length),
  };
}

// ─── component ────────────────────────────────────────────────────────────────

type PhoneNumberInputProps = {
  value: string;
  onChange: (value: string) => void;
  dialCode: string;
  onDialCodeChange: (dialCode: string) => void;
  error?: string;
  placeholder?: string;
  singleCountry?: boolean;
};

export function PhoneNumberInput({
  value,
  onChange,
  dialCode,
  onDialCodeChange,
  error,
  placeholder,
  singleCountry = false,
}: PhoneNumberInputProps) {
  const country =
    GCC_COUNTRIES.find((c) => c.dialCode === dialCode) ?? GCC_COUNTRIES[0];
  const firstDigit = convertArabicDigitsToEnglish(value?.[0] ?? "");
  const mask = getActiveMask(dialCode, firstDigit);
  const definitions = getDefinitions(dialCode);

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text");

    // International number: auto-select dial code and strip it.
    const detected = detectGccDialCode(pasted);
    if (detected) {
      e.preventDefault();
      onDialCodeChange(detected.dialCode);
      onChange(detected.localNumber);
      return;
    }

    // SA/UAE 0-prefix local number (e.g. "0564833990" = 10 digits).
    // Set the value directly so the 10-digit mask activates before IMask has a
    // chance to truncate through the currently-active 9-digit mask.
    const english = convertArabicDigitsToEnglish(pasted.trim()).replace(
      /\D/g,
      "",
    );
    if ((dialCode === "+966" || dialCode === "+971") && english[0] === "0") {
      e.preventDefault();
      onChange(english.slice(0, 10));
    }
  }

  return (
    <div
      className={`flex rounded-xl border overflow-hidden transition ${
        error
          ? "border-red-400 bg-red-50 focus-within:ring-2 focus-within:ring-red-200"
          : "border-slate-200 bg-slate-50 focus-within:ring-2 focus-within:ring-primary/25 focus-within:border-primary"
      }`}
    >
      {/* Country selector — border-e is logical (flips in RTL automatically) */}
      <div className="relative shrink-0">
        <select
          value={dialCode}
          onChange={(e) => {
            onDialCodeChange(e.target.value);
            onChange("");
          }}
          className={`appearance-none h-full pl-3.5 pr-7 text-sm font-medium text-slate-600 bg-slate-100 border-e border-slate-200 focus:outline-none`}
        >
          {GCC_COUNTRIES.map((c) => (
            <option key={c.dialCode} value={c.dialCode}>
              {c.flag} {c.dialCode}
            </option>
          ))}
        </select>
        {!singleCountry && (
          // end-2 = inset-inline-end: correct side in both LTR and RTL
          <ChevronDown className="absolute end-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400 pointer-events-none" />
        )}
      </div>

      {/*
        dir="ltr" keeps digit entry left-to-right even inside an RTL page.
        key={dialCode} forces a clean IMask remount on country change so no
        stale mask state bleeds across countries.
        mask changes reactively (via getActiveMask) for SA/UAE 9↔10 switching
        without a remount, which preserves cursor position while typing.
      */}
      <IMaskInput
        key={dialCode}
        mask={mask}
        {...(definitions && { definitions })}
        prepare={prepareChar}
        unmask
        value={value}
        onAccept={(val) =>
          onChange(convertArabicDigitsToEnglish(val as string))
        }
        onPaste={handlePaste}
        inputMode="numeric"
        dir="ltr"
        placeholder={placeholder ?? country.placeholder}
        className="flex-1 bg-transparent px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none rtl:placeholder:text-right!"
      />
    </div>
  );
}
